/**
 * AssistantLauncher — the global Assistant entry point for the in-app preview.
 *
 * A floating button on the Run/Preview surface; clicking it opens the shared
 * <AssistantChat> in a docked panel. Renders nothing when the project has no
 * enabled Assistant, so apps that don't configure one are unaffected.
 */
import { useEffect, useState } from 'react'
import { claimDock, dockedBottom } from '@twasta/page-runtime/floatingDock'
import { MessageSquare, X, Maximize2, Minimize2, Plus, BarChart3 } from 'lucide-react'
import { assistantConfig, type AssistantConfig } from '@/api/assistant'
import { AssistantChat } from './AssistantChat'
import type { OpenTargetHandler } from '@twasta/page-runtime/search'
import { SavedVisualsPanel } from './SavedVisuals'

export function AssistantLauncher({ project, objName, onOpen }: {
  project: string
  /** The object on screen. Passed straight through to the chat, which sends
   *  it so a repository screen scopes document search to its own library. */
  objName?: string
  /** How this host opens a citation — the SAME seam application-wide
   *  search navigates through, so a cited record lands where a search
   *  hit for it would. Omitted, citations fall back to plain links. */
  onOpen?: OpenTargetHandler
}) {
  const [cfg, setCfg] = useState<AssistantConfig | null>(null)
  const [open, setOpen] = useState(false)
  const [maximized, setMaximized] = useState(false)
  // Once opened, the panel stays MOUNTED (just hidden with CSS) so the whole
  // conversation is retained across close → reopen. Only a New session
  // (sessionKey bump) or a full page reload clears it.
  const [everOpened, setEverOpened] = useState(false)
  // Bumping this remounts <AssistantChat>, wiping its conversation for a fresh
  // session — cheaper and less error-prone than threading a reset callback in.
  const [sessionKey, setSessionKey] = useState(0)
  // Which body the panel shows: the live chat or the saved-visuals browser.
  const [view, setView] = useState<'chat' | 'saved'>('chat')

  // The launcher sits over the bottom-right corner for as long as this app
  // HAS an Assistant, so anything laid out to the right edge (a transaction
  // list's pagination row) reserves room for it rather than running underneath
  // — see `floatingDock`. Gated on `enabled`, because an app without an
  // Assistant must not have a hole reserved for a button that never appears.
  useEffect(() => {
    if (!cfg?.enabled) return undefined
    return claimDock({ right: 78 })
  }, [cfg?.enabled])

  const openPanel = () => { setOpen(true); setEverOpened(true) }
  const newSession = () => {
    if (window.confirm('Start a new conversation? This clears the current chat.')) {
      setSessionKey((k) => k + 1); setView('chat')
    }
  }

  // Escape restores a maximized panel to its docked size.
  useEffect(() => {
    if (!maximized) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMaximized(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [maximized])

  useEffect(() => {
    let cancelled = false
    if (!project) return
    assistantConfig(project)
      .then((c) => { if (!cancelled) setCfg(c) })
      .catch(() => { if (!cancelled) setCfg({ enabled: false }) })
    return () => { cancelled = true }
  }, [project])

  if (!cfg?.enabled) return null
  const name = cfg.display_name || 'Assistant'

  return (
    <>
      {!open && (
        <button onClick={openPanel} aria-label={`Open ${name}`} style={fab}>
          <MessageSquare size={22} />
        </button>
      )}
      {everOpened && (
        <div style={{ ...(maximized ? panelMax : panel), display: open ? 'flex' : 'none' }}>
          <div style={panelHdr}>
            <MessageSquare size={16} />
            <span style={{ fontWeight: 700, fontSize: 15 }}>{name}</span>
            <div style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <CtrlBtn onClick={newSession} title="Start a new conversation" label="New chat" showLabel>
                <Plus size={16} />
              </CtrlBtn>
              <CtrlBtn
                onClick={() => setView((v) => (v === 'saved' ? 'chat' : 'saved'))}
                title="My saved visuals"
                label="Saved"
                showLabel={maximized}
                active={view === 'saved'}
              ><BarChart3 size={15} /></CtrlBtn>
              <CtrlBtn
                onClick={() => setMaximized((v) => !v)}
                title={maximized ? 'Restore' : 'Maximize'}
                label={maximized ? 'Restore' : 'Maximize'}
                showLabel={maximized}
              >{maximized ? <Minimize2 size={15} /> : <Maximize2 size={15} />}</CtrlBtn>
              <CtrlBtn onClick={() => setOpen(false)} title="Close" label="Close" showLabel={maximized}>
                <X size={16} />
              </CtrlBtn>
            </div>
          </div>
          <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
            <div style={{ height: '100%', display: view === 'chat' ? 'block' : 'none' }}>
              <AssistantChat
                key={sessionKey}
                project={project}
                objName={objName}
                greeting={cfg.greeting}
                suggestedQuestions={cfg.suggested_questions}
                height="100%"
                onOpen={onOpen}
              />
            </div>
            {view === 'saved' && (
              <div style={{ position: 'absolute', inset: 0 }}>
                <SavedVisualsPanel project={project} canStandardize onBack={() => setView('chat')} />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}

// Header window control — icon-only in the docked panel (to save width); icon +
// text label when `showLabel` (the + button always; the rest when maximized, so
// the fullscreen view has an unmistakable Restore / Close affordance).
function CtrlBtn({
  onClick, title, label, showLabel = false, active = false, children,
}: {
  onClick: () => void
  title: string
  label: string
  showLabel?: boolean
  active?: boolean
  children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick} aria-label={title} title={title}
      style={{
        ...iconBtn,
        ...(showLabel ? { gap: 6, padding: '4px 9px' } : {}),
        ...(active ? { background: 'rgba(255,255,255,0.22)' } : {}),
      }}
    >
      {children}
      {showLabel && <span style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>{label}</span>}
    </button>
  )
}

const fab: React.CSSProperties = {
  position: 'fixed', right: 22, bottom: dockedBottom(22), zIndex: 1200,
  width: 56, height: 56, borderRadius: '50%', border: 'none',
  background: '#4f46e5', color: '#fff', cursor: 'pointer',
  boxShadow: '0 6px 20px rgba(79,70,229,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center',
}
const panel: React.CSSProperties = {
  position: 'fixed', right: 22, bottom: dockedBottom(22), zIndex: 1200,
  width: 'min(420px, calc(100vw - 32px))', height: 'min(620px, calc(100vh - 100px))',
  display: 'flex', flexDirection: 'column', background: '#fff', color: '#1f2937',
  border: '1px solid #e5e7eb', borderRadius: 16, overflow: 'hidden',
  boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
}
const panelHdr: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px',
  background: '#4f46e5', color: '#fff',
}
const iconBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer',
  padding: 4, borderRadius: 6,
}
const panelMax: React.CSSProperties = {
  // Above any app chrome (the runtime shell's sticky top bar is z 9000) so the
  // fullscreen panel — and its Restore/Close controls — is never covered.
  position: 'fixed', inset: 0, zIndex: 9600,
  width: '100vw', height: '100vh',
  display: 'flex', flexDirection: 'column', background: '#fff', color: '#1f2937',
  border: 'none', borderRadius: 0, overflow: 'hidden',
}
